import { computed, ComputedRef, getCurrentInstance } from 'vue';
import { RouteLocationNormalizedLoaded, Router, useRoute, useRouter } from 'vue-router';
import { createSearchDataSource, Openapi } from '../connections';
import { uniqueID } from '../unique-id';
import { Dictionary } from 'lodash';

export interface IAppHelpers {
  $app: ComputedRef<any>;
  $fn: ComputedRef<any>;
  $i18n: ComputedRef<any>;
  $store: ComputedRef<any>;
  $config: ComputedRef<any>;
  $imodal: ComputedRef<any>;
  $notification: ComputedRef<any>;
  $tabs: ComputedRef<any>;
  $filters: ComputedRef<any>;
  $route: RouteLocationNormalizedLoaded;
  $router: Router;
  gettext: (...args: any[]) => string;
  pgettext: (...args: any[]) => string;
  ngettext: (...args: any[]) => string;
  npgettext: (...args: any[]) => string;
  getSchema: (name: string) => any;
  searchDataSource: (operation: string, parameters?: Dictionary<any>) => any;
  randomElementId: () => string;
  schemaDefaults: (name: string, extras?: object) => any;
  datetime: (value: any, opts?: any) => any;
  date: (value: any, opts?: any) => any;
  time: (value: any, opts?: any) => any;
  number: (value: any, opts?: any) => any;
  money: (value: any, currency?: string, digits?: number) => any;
  distance: (value: any) => any;
  username: (value: any) => any;
  filesize: (value: any) => any;
}

export function useHelpers(): IAppHelpers {
  const instance = getCurrentInstance();
  if (!instance) {
    throw new Error('useHelpers must be called within setup()');
  }

  const proxy = instance.proxy as any;
  const root = proxy.$root;

  const $app = computed(() => root.$app);

  const $fn = computed(() => root.$fn);

  const $i18n = computed(() => root.$i18n);

  const $store = computed(() => root.$store);

  const $config = computed(() => root.$config);

  const $imodal = computed(() => root.$imodal);

  const $notification = computed(() => root.$notification);

  const $tabs = computed(() => root.$tabs);

  const $filters = computed(() => root.$filters);

  const $route = useRoute();

  const $router = useRouter();

  const gettext = (...args: any[]) => {
    return $fn.value.gettext(...args);
  };

  const pgettext = (...args: any[]) => {
    return $fn.value.pgettext(...args);
  };

  const ngettext = (...args: any[]) => {
    return $fn.value.ngettext(...args);
  };

  const npgettext = (...args: any[]) => {
    return $fn.value.npgettext(...args);
  };

  const getSchema = (name: string) => {
    return ($app.value.connections.default as Openapi).getSchema(name);
  };

  const searchDataSource = (
    operation: string,
    parameters?: Dictionary<any>,
  ) => {
    return createSearchDataSource<object>(
      $app.value.connections.default as Openapi,
      operation,
      parameters,
    );
  };

  const randomElementId = () => {
    return uniqueID();
  };

  const schemaDefaults = (name: string, extras?: object) => {
    return $fn.value.schemaDefaults(name, extras);
  };

  const datetime = (value: any, opts?: any) => {
    return $filters.value?.datetime?.(value, opts) ?? value;
  };

  const date = (value: any, opts?: any) => {
    return $filters.value?.date?.(value, opts) ?? value;
  };

  const time = (value: any, opts?: any) => {
    return $filters.value?.time?.(value, opts) ?? value;
  };

  const number = (value: any, opts?: any) => {
    return $filters.value?.number?.(value, opts) ?? value;
  };

  const money = (value: any, currency?: string, digits?: number) => {
    return $filters.value?.money?.(value, currency, digits) ?? value;
  };

  const distance = (value: any) => {
    return $filters.value?.distance?.(value) ?? value;
  };

  const username = (value: any) => {
    return $filters.value?.username?.(value) ?? value;
  };

  const filesize = (value: any) => {
    return $filters.value?.filesize?.(value) ?? value;
  };

  return {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $filters,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    getSchema,
    searchDataSource,
    randomElementId,
    schemaDefaults,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  };
}
